// src/components/manual/ManualDrawer.tsx
// 「このタブの見方」= 右からスライドインするマニュアルのドロワー。
//   - 表示専用。中身は content.tsx の静的レジストリから引く。
//   - props: tabKey / open / onClose(制御コンポーネント)。
//   - 自前で開閉状態を持つ <ManualButton tabKey> も併せて公開(配線を1行にするため)。
//   - 印刷では出さない(no-print)。styles.css の card クラス流儀に合わせる。
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { TabKey } from "../../store";
import { getManual } from "./content";

export function ManualDrawer({
  tabKey,
  open,
  onClose,
}: {
  tabKey: TabKey;
  open: boolean;
  onClose: () => void;
}) {
  // Esc で閉じる。open のときだけ listener を張る。
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const m = getManual(tabKey);

  return (
    <div
      className={[
        "no-print fixed inset-0 z-40",
        open ? "" : "pointer-events-none",
      ].join(" ")}
      aria-hidden={!open}
    >
      {/* 背景オーバーレイ(クリックで閉じる) */}
      <div
        onClick={onClose}
        className={[
          "absolute inset-0 bg-slate-900/30 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />
      {/* 右から出るパネル */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="このタブの見方"
        className={[
          "absolute top-0 right-0 h-full w-full sm:w-[440px] bg-white shadow-xl",
          "flex flex-col transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        {/* ヘッダ */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 bg-emerald-50/60 shrink-0">
          <span className="text-lg">{m?.icon ?? "📖"}</span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">
              {m ? `${m.label} の見方` : "このタブの見方"}
            </div>
            <div className="text-[11px] text-slate-500">データ・用語・読み方の注意</div>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-slate-400 hover:text-slate-700 text-xl leading-none px-1"
            title="閉じる (Esc)"
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        {/* 本体(スクロール) */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 text-sm text-slate-700">
          {!m ? (
            <p className="text-slate-500">このタブの説明はまだ用意されていません。</p>
          ) : (
            <>
              <Section title="このタブで分かること">
                <p className="leading-relaxed">{m.summary}</p>
              </Section>

              <Section title="データの源">
                <p className="leading-relaxed">{m.dataSource}</p>
              </Section>

              <Section title="用語解説">
                <dl className="space-y-2">
                  {m.terms.map((t) => (
                    <div key={t.term} className="border-l-2 border-l-brand/40 pl-2.5">
                      <dt className="font-semibold text-slate-800">{t.term}</dt>
                      <dd className="text-[13px] leading-relaxed text-slate-600">{t.desc}</dd>
                    </div>
                  ))}
                </dl>
              </Section>

              <Section title="読み方の注意">
                <ul className="space-y-2">
                  {m.cautions.map((c, i) => (
                    <li
                      key={i}
                      className="flex gap-2 text-[13px] leading-relaxed rounded bg-amber-50 border border-amber-100 px-2.5 py-2"
                    >
                      <span className="shrink-0 text-amber-600" aria-hidden>⚠</span>
                      <span className="text-slate-700">{c}</span>
                    </li>
                  ))}
                </ul>
              </Section>

              <Section title="更新タイミング">
                <p className="leading-relaxed text-slate-600">{m.update}</p>
              </Section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * 自己完結の「📖 このタブの見方」ボタン+ドロワー。
 * 配線は <ManualButton tabKey={f.tab} /> の1行で済む(開閉状態は内部で保持)。
 * そのタブのマニュアルが無い場合は何も描画しない。
 */
export function ManualButton({
  tabKey,
  className = "",
}: {
  tabKey: TabKey;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!getManual(tabKey)) return null;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={[
          "no-print inline-flex items-center gap-1 text-xs px-2 py-1 rounded",
          "border border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-900",
          className,
        ].join(" ")}
        title="このタブのデータ・用語・注意点の説明を開く"
      >
        <span aria-hidden>📖</span>
        <span>このタブの見方</span>
      </button>
      <ManualDrawer tabKey={tabKey} open={open} onClose={() => setOpen(false)} />
    </>
  );
}
